import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/** Applications submitted by this Person (Forms submissions with source=application) + attribution. */
export function PersonApplicationsPanel({ personId, Card }) {
  const [items, setItems] = useState(null);
  const [view, setView] = useState(null);
  const [pdf, setPdf] = useState(null);
  useEffect(() => () => { if (pdf) { URL.revokeObjectURL(pdf.url); } }, [pdf]);
  useEffect(() => { api.get(`/persons/${personId}/applications`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [personId]);
  const open = async (a) => { try { const { data } = await api.get(`/persons/${personId}/applications/${a.id}`); setView(data.response); } catch (e) { toast.error(apiError(e)); } };
  const download = async (r, a) => {
    try {
      const res = await api.get(`/persons/${personId}/applications/${r.id}/files/${a.upload_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const el = document.createElement("a"); el.href = url; el.download = a.name; el.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Couldn't download the file"); }
  };
  const preview = async (r, a) => {
    try {
      const res = await api.get(`/persons/${personId}/applications/${r.id}/files/${a.upload_id}?preview=true`, { responseType: "blob" });
      setPdf({ url: URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), name: a.name, r, a });
    } catch (e) { toast.error("Couldn't open the preview"); }
  };
  const isPdf = (a) => /\.pdf$/i.test(a?.name || "");
  return (
    <Card title="Applications" testId="panel-applications">
      {items === null ? <p className="text-sm text-slate-400">Loading…</p> : !items.length ? <p className="text-sm text-slate-500">No applications submitted.</p> : (
        <div className="space-y-2">{items.map((a) => (
          <div key={a.id} className="rounded-md border border-slate-200 p-3 text-sm flex flex-wrap items-start justify-between gap-2" data-testid={`application-${a.id}`}>
            <div className="space-y-0.5">
              <p className="font-medium text-slate-800">{a.form_name} · {new Date(a.submitted_at).toLocaleDateString()}</p>
              <p className="text-xs text-slate-600">Interest: {a.program_interest || "—"} · Status: {a.status}</p>
              <p className="text-xs text-slate-600" data-testid={`application-attribution-${a.id}`}>Referral source: {a.attribution?.referral_source || "—"}{a.attribution?.referral_code ? ` (code ${a.attribution.referral_code})` : ""} · Assigned recruiter at intake: {a.attribution?.assigned_recruiter_name || "Unassigned"}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => open(a)} data-testid={`application-view-${a.id}`}>View Application</Button>
          </div>))}</div>)}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="application-view-dialog">
          <DialogHeader><DialogTitle>{view?.form_name}</DialogTitle></DialogHeader>
          <dl className="space-y-2 text-sm">{(view?.questions || []).map((q) => (
            <div key={q.id}><dt className="text-xs text-slate-500">{q.label}</dt><dd className="text-slate-800">{q.field_type === "file_upload" ? (view.answers?.[q.id]?.upload_id
              ? <div className="flex flex-wrap gap-2">
                  {isPdf(view.answers[q.id]) && <Button size="sm" className="bg-[#006090] hover:bg-[#004E75]" onClick={() => preview(view, view.answers[q.id])} data-testid={`application-file-preview-${view.answers[q.id].upload_id}`}>Preview</Button>}
                  <Button size="sm" variant="outline" onClick={() => download(view, view.answers[q.id])} data-testid={`application-file-${view.answers[q.id].upload_id}`}>Download {view.answers[q.id].name}</Button>
                </div> : "—")
              : [].concat(view.answers?.[q.id] ?? "—").join(", ") || "—"}</dd></div>))}</dl>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pdf} onOpenChange={(o) => !o && setPdf(null)}>
        <DialogContent className="bg-white max-w-4xl w-[95vw]" data-testid="application-pdf-preview-dialog">
          <DialogHeader><DialogTitle className="truncate pr-6">{pdf?.name}</DialogTitle></DialogHeader>
          {pdf && <iframe src={pdf.url} title="Resume preview" className="w-full h-[75vh] rounded border border-slate-200" data-testid="application-pdf-frame" />}
          <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => download(pdf.r, pdf.a)} data-testid="application-pdf-download">Download</Button></div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
